import fixturePicture from '@assets/report-samples/fixture.jpg';
import overviewPicture from '@assets/report-samples/overview.jpg';
import seamPicture from '@assets/report-samples/seam.jpg';
import thumbnailPicture from '@assets/report-samples/thumbnail.png';
import { noticeTypesBySeverity } from '@lib/spproject/inspectionStatus';
import { type Inspection } from '@lib/spproject/types';
import { type LocalizedText } from '@store/api/poiTypeEndpoints';

/**
 * The inspection the canvas is drawn from.
 *
 * A mirror, kept by hand, of `sampleInspection` in the backend's
 * `core/utils/report/sample-inspection.ts`, which is what the PDF preview is drawn from when
 * its author picked no inspection: the same project and inspection, the same five checkpoints
 * and one note with their steps, statuses and comments, the same photos, in English and in
 * Turkish as the server has them. So a page composed here reads the same in the PDF tab. A
 * text changed there is changed here, and so is a picture: the four under
 * `@assets/report-samples` are copies of the files in the backend's `assets/samples`, which
 * this package cannot reach.
 *
 * One thing is not carried over. The answers to a POI type keep only what the canvas draws of
 * them, a label and a value each: the group one of them stands in on a report, with its
 * heading, is left out.
 *
 * `summaryOf` beside it mirrors `summarizeInspection` (`report/pdf/summary.ts`) for what the
 * templates read: the counts, the completion and the verdict.
 *
 * The checkpoints have every status between them, and `hasResultsPage` in `print.ts` counts
 * on it: no status filter of a design leaves the sample without a point to list.
 */

type NoticeType = Inspection.NoticeType;

/** A picture of the sample: what its caption prints, and where the picture itself is. */
export interface SamplePhoto {
  name: string;
  taken: Date;
  /** The address of the picture, for an `img`. */
  picture: string;
}

/** One answer to a field of a step's POI type. A text by locale is a choice's own label. */
export interface SampleAnswer {
  label: LocalizedText;
  value: LocalizedText | string | number | boolean;
}

export interface SampleStep {
  description: string;
  comment: string;
  notice: NoticeType;
  modified: Date;
  photos: SamplePhoto[];
  /** The step's POI type by name, with the answers a report prints. */
  answers?: { name: LocalizedText; fields: SampleAnswer[] };
}

export interface SamplePoint {
  /** The number the point goes by on a report: its `order`. */
  number: number;
  name: string;
  steps: SampleStep[];
}

export interface Sample {
  projectName: string;
  inspectionName: string;
  created: Date;
  modified: Date;
  /** What a report prints as the operator: the name of the device the points were recorded on. */
  operator: string;
  checkpoints: SamplePoint[];
  notes: SamplePoint[];
  /** The inspection's own pictures, which a snapshot grid prints. */
  snapshots: SamplePhoto[];
  /** The address of the project's thumbnail. */
  thumbnail: string;
}

const at = (time: string) => new Date(`2026-01-15T${time}:00`);

// By the name the server's sample gives each, which a caption prints.
const pictures = { 'seam.jpg': seamPicture, 'fixture.jpg': fixturePicture, 'overview.jpg': overviewPicture };

const photo = (name: keyof typeof pictures, time: string): SamplePhoto => ({ name, taken: at(time), picture: pictures[name] });

const step = (
  description: string, notice: NoticeType, comment = '', rest: Pick<SampleStep, 'answers'> & { photos?: SamplePhoto[] } = {},
): SampleStep => ({ description, comment, notice, modified: at('10:45'), photos: [], ...rest });

/** The answers of the electrical panel, as a report prints them: every field shown on reports, the hidden group's too. */
const panelAnswers: NonNullable<SampleStep['answers']> = {
  name: { en: 'Electrical panel', tr: 'Elektrik panosu' },
  fields: [
    { label: { en: 'Voltage class', tr: 'Gerilim sınıfı' }, value: { en: 'Medium voltage', tr: 'Orta gerilim' } },
    { label: { en: 'Hotspot temperature (°C)', tr: 'Sıcak nokta (°C)' }, value: 72.5 },
    { label: { en: 'Qualified person ID', tr: 'Yetkili kişi sicili' }, value: 'QP-1042' },
    { label: { en: 'Shutdown required', tr: 'Enerji kesilmeli' }, value: false },
  ],
};

/**
 * The sample in the language of the interface: Turkish for a Turkish reader, English for
 * everybody else, which is the server's own choice for the language a preview is asked in.
 */
export const sampleOf = (language: string): Sample => {
  const turkish = /^tr(?:[-_]|$)/i.test(language);
  const say = (en: string, tr: string) => (turkish ? tr : en);

  return {
    projectName: say('Sample project', 'Örnek proje'),
    inspectionName: say('Sample inspection', 'Örnek kontrol'),
    created: at('08:15'),
    modified: at('11:00'),
    operator: 'iPad-QA-01',
    checkpoints: [
      {
        number: 1,
        name: say('Weld seam continuity', 'Kaynak dikişi sürekliliği'),
        steps: [
          step(say('Check the seam by eye along its whole length', 'Dikişi tüm uzunluğu boyunca gözle kontrol edin'), 'Ok'),
          step(
            say('Run the penetrant test at the root of the seam', 'Dikişin kökünde penetrant testi uygulayın'), 'Error',
            say('Crack of about 4 mm near the rear pin', 'Arka pim yakınında yaklaşık 4 mm çatlak'),
            { photos: [photo('seam.jpg', '09:20')] },
          ),
          step(say('Clean the surface after the test', 'Testten sonra yüzeyi temizleyin'), 'Ok'),
        ],
      },
      {
        number: 2,
        name: say('Fixture position', 'Fikstür konumu'),
        steps: [
          step(
            say('Check that the fixture sits against both stops', 'Fikstürün iki dayamaya da oturduğunu kontrol edin'),
            'Warning', say('Offset of about 2 mm at the right stop', 'Sağ dayamada yaklaşık 2 mm kaçıklık'),
            { photos: [photo('fixture.jpg', '09:50')] },
          ),
        ],
      },
      {
        number: 3,
        name: say('Electrical panel', 'Elektrik panosu'),
        steps: [
          step(
            say('Scan the panel with the thermal camera', 'Panoyu termal kamerayla tarayın'), 'Warning',
            say(
              'Hot spot at the main breaker, to be measured again under load',
              'Ana şalterde sıcak nokta, yük altında yeniden ölçülecek',
            ),
            { answers: panelAnswers },
          ),
        ],
      },
      {
        // Half done: its one finished step is no reason to count it as passed.
        number: 4,
        name: say('Surface finish', 'Yüzey kalitesi'),
        steps: [
          step(say('Measure the roughness of the first face', 'Birinci yüzeyin pürüzlülüğünü ölçün'), 'Ok'),
          step(say('Measure the roughness of the second face', 'İkinci yüzeyin pürüzlülüğünü ölçün'), 'NotChecked'),
        ],
      },
      {
        number: 5,
        name: say('Type label', 'Tip etiketi'),
        steps: [
          step(
            say(
              'Check that the label is legible and matches the order',
              'Etiketin okunaklı olduğunu ve siparişle eşleştiğini kontrol edin',
            ),
            'Ok', say('Matches the order', 'Siparişle eşleşiyor'),
          ),
        ],
      },
    ],
    notes: [
      {
        number: 6,
        name: say('Oil trace near the flange', 'Flanş yakınında yağ izi'),
        steps: [
          step('', 'Warning', say('Seen on the walk-around, outside the checklist', 'Çevre turunda görüldü, kontrol listesi dışında')),
        ],
      },
    ],
    snapshots: [photo('overview.jpg', '10:40')],
    thumbnail: thumbnailPicture,
  };
};

/** A point's status: the worst of its steps, as everything that counts points reads it. */
export const pointNotice = (point: SamplePoint): NoticeType =>
  noticeTypesBySeverity.find((notice) => point.steps.some((entry) => entry.notice === notice)) ?? 'NotChecked';

/** What the report says the inspection amounts to. */
export type SampleVerdict = 'attention' | 'clear' | 'incomplete' | 'empty';

export interface SampleSummary {
  /** Checkpoints by their status. Notes are never counted. */
  noticeCounts: Record<NoticeType, number>;
  countedPoints: number;
  /** Checkpoints whose every step has been looked at. */
  finishedPoints: number;
  completionPercent: number;
  /** Checkpoints whose status is an error or a warning. */
  findings: number;
  /** The status whose colours the verdict is set in. */
  verdictNotice: NoticeType;
  verdict: SampleVerdict;
}

/**
 * The summary of a sample by the server's rules: checkpoints only, completion as the share of
 * checkpoints with every step looked at, held one short of 100 and of 0 while it only rounds
 * to them, and a verdict that says "clear" only of an inspection that is finished.
 */
export const summaryOf = ({ checkpoints }: Sample): SampleSummary => {
  const noticeCounts: Record<NoticeType, number> = { NotChecked: 0, Ok: 0, Warning: 0, Error: 0 };

  checkpoints.forEach((point) => { noticeCounts[pointNotice(point)] += 1; });

  const countedPoints = checkpoints.length;
  const finishedPoints = checkpoints.filter((point) => point.steps.every((entry) => entry.notice !== 'NotChecked')).length;
  const findings = noticeCounts.Error + noticeCounts.Warning;
  const rounded = countedPoints === 0 ? 0 : Math.round((finishedPoints / countedPoints) * 100);

  let verdict: SampleVerdict = 'incomplete';

  if (countedPoints === 0) verdict = 'empty';
  else if (findings > 0) verdict = 'attention';
  else if (finishedPoints === countedPoints) verdict = 'clear';

  let verdictNotice: NoticeType = 'NotChecked';

  if (verdict === 'attention') verdictNotice = noticeCounts.Error > 0 ? 'Error' : 'Warning';
  if (verdict === 'clear') verdictNotice = 'Ok';

  return {
    noticeCounts,
    countedPoints,
    finishedPoints,
    completionPercent: finishedPoints < countedPoints ? Math.min(99, Math.max(finishedPoints > 0 ? 1 : 0, rounded)) : rounded,
    findings,
    verdictNotice,
    verdict,
  };
};
